import React, { useRef, useEffect } from 'react';
import { Bell, X, CheckCheck, AlertTriangle, Info, CheckCircle, TrendingUp, Calendar } from 'lucide-react';
import { useUserData } from '../context/UserDataContext';

// ── Notification generators ─────────────────────────────────────────────────

function generateNotifications({ budgetData, netWorthData, goalsData, retirementData, calendarData }) {
  const notifications = [];
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);

  // 1. Over-budget categories
  (budgetData?.categories || []).forEach(c => {
    if (c.budgeted > 0 && c.spent > c.budgeted) {
      notifications.push({
        id: `over-budget-${c.name}`,
        type: 'danger',
        icon: 'alert',
        title: `Over budget: ${c.name}`,
        desc: `You've spent $${Math.round(c.spent).toLocaleString()} vs your $${Math.round(c.budgeted).toLocaleString()} budget.`,
        tab: 'budget',
        ts: Date.now(),
      });
    } else if (c.budgeted > 0 && c.spent / c.budgeted >= 0.85) {
      notifications.push({
        id: `near-budget-${c.name}`,
        type: 'warning',
        icon: 'alert',
        title: `Nearing limit: ${c.name}`,
        desc: `${Math.round((c.spent / c.budgeted) * 100)}% of budget used. Only $${Math.round(c.budgeted - c.spent).toLocaleString()} left.`,
        tab: 'budget',
        ts: Date.now() - 1000,
      });
    }
  });

  // 2. Upcoming calendar events (within 7 days)
  (calendarData || []).forEach(ev => {
    if (ev.date >= todayStr) {
      const daysUntil = Math.ceil((new Date(ev.date + 'T12:00:00') - today) / (1000 * 60 * 60 * 24));
      if (daysUntil <= 7) {
        notifications.push({
          id: `upcoming-${ev.date}-${ev.title}`,
          type: 'info',
          icon: 'calendar',
          title: `Upcoming: ${ev.title}`,
          desc: `${daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}${ev.amount ? ` · ${ev.amount > 0 ? '+' : ''}$${Math.abs(ev.amount).toLocaleString()}` : ''}`,
          tab: 'calendar',
          ts: Date.now() - 2000,
        });
      }
    }
  });

  // 3. Goals milestones
  (goalsData || []).forEach(g => {
    const pct = g.target > 0 ? (g.current || 0) / g.target : 0;
    if (pct >= 1) {
      notifications.push({
        id: `goal-complete-${g.id}`,
        type: 'success',
        icon: 'success',
        title: `Goal achieved: ${g.name}! 🎉`,
        desc: `You hit your ${g.target ? `$${g.target.toLocaleString()}` : ''} goal. Congratulations!`,
        tab: 'goals',
        ts: Date.now() - 3000,
      });
    } else if (pct >= 0.75 && pct < 1) {
      notifications.push({
        id: `goal-75-${g.id}`,
        type: 'info',
        icon: 'trending',
        title: `75% toward ${g.name}`,
        desc: `Great progress! You're ${Math.round(pct * 100)}% of the way to your goal.`,
        tab: 'goals',
        ts: Date.now() - 4000,
      });
    }
  });

  // 4. Retirement success rate
  if (retirementData?.successRate > 0 && retirementData.successRate < 70) {
    notifications.push({
      id: 'retirement-low',
      type: 'danger',
      icon: 'alert',
      title: 'Retirement success rate is low',
      desc: `Your Monte Carlo success rate is ${retirementData.successRate}%. Consider increasing savings or adjusting your plan.`,
      tab: 'retirement',
      ts: Date.now() - 5000,
    });
  }

  // 5. Emergency fund check
  const totalCash = (netWorthData?.assets || [])
    .filter(a => a.type === 'Cash' || a.type === 'Savings')
    .reduce((s, a) => s + (a.value || 0), 0);
  const monthlyExpenses = (budgetData?.categories || []).reduce((s, c) => s + (c.spent || 0), 0);
  if (monthlyExpenses > 0 && totalCash < monthlyExpenses * 3) {
    const months = totalCash > 0 ? (totalCash / monthlyExpenses).toFixed(1) : 0;
    notifications.push({
      id: 'emergency-fund',
      type: 'warning',
      icon: 'alert',
      title: 'Low emergency fund',
      desc: `You have ~${months} month${months === '1.0' ? '' : 's'} of expenses saved. Aim for 3–6 months.`,
      tab: 'networth',
      ts: Date.now() - 6000,
    });
  }

  return notifications.sort((a, b) => {
    const priority = { danger: 0, warning: 1, success: 2, info: 3 };
    return (priority[a.type] ?? 4) - (priority[b.type] ?? 4);
  });
}

// ── Icon map ─────────────────────────────────────────────────────────────────

function NotifIcon({ type, icon }) {
  const map = {
    alert:    { icon: <AlertTriangle size={14} />, classes: 'bg-amber-500/20 text-amber-400' },
    calendar: { icon: <Calendar size={14} />,      classes: 'bg-blue-500/20 text-blue-400' },
    success:  { icon: <CheckCircle size={14} />,   classes: 'bg-emerald-500/20 text-emerald-400' },
    trending: { icon: <TrendingUp size={14} />,    classes: 'bg-purple-500/20 text-purple-400' },
    info:     { icon: <Info size={14} />,          classes: 'bg-blue-500/20 text-blue-400' },
  };
  const style = map[icon] || map.info;
  if (type === 'danger') return (
    <div className="w-8 h-8 rounded-full bg-rose-500/20 text-rose-400 flex items-center justify-center flex-shrink-0">
      <AlertTriangle size={14} />
    </div>
  );
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${style.classes}`}>
      {style.icon}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function NotificationPanel({ onClose, setActiveTab }) {
  const {
    budgetData, netWorthData, goalsData, retirementData, calendarData,
    userProfile, updateSection,
  } = useUserData();

  const panelRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handle(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  const notifications = generateNotifications({ budgetData, netWorthData, goalsData, retirementData, calendarData });
  const readIds = userProfile?.readNotificationIds || [];
  const unreadCount = notifications.filter(n => !readIds.includes(n.id)).length;

  const markAllRead = () => {
    const allIds = notifications.map(n => n.id);
    updateSection('user_profile', { ...userProfile, readNotificationIds: allIds });
  };

  const markRead = (id) => {
    if (!readIds.includes(id)) {
      updateSection('user_profile', {
        ...userProfile,
        readNotificationIds: [...readIds, id],
      });
    }
  };

  const handleNotifClick = (n) => {
    markRead(n.id);
    if (n.tab && setActiveTab) setActiveTab(n.tab);
    onClose();
  };

  return (
    <div
      ref={panelRef}
      className="fixed top-14 right-4 w-80 sm:w-96 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl shadow-black/40 z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Bell size={15} className="text-slate-400" />
          <span className="text-sm font-semibold text-slate-200">Notifications</span>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 bg-rose-500 text-white text-xs rounded-full font-medium">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
              title="Mark all as read"
            >
              <CheckCheck size={13} /> All read
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-h-96 overflow-y-auto divide-y divide-slate-800/60">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-center px-4">
            <CheckCircle size={28} className="text-emerald-400 mb-2" />
            <p className="text-slate-300 text-sm font-medium">All clear!</p>
            <p className="text-xs text-slate-500 mt-0.5">No alerts at the moment.</p>
          </div>
        ) : (
          notifications.map(n => {
            const isRead = readIds.includes(n.id);
            return (
              <button
                key={n.id}
                onClick={() => handleNotifClick(n)}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-800/60 transition-colors ${
                  isRead ? 'opacity-60' : ''
                }`}
              >
                <NotifIcon type={n.type} icon={n.icon} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <span className={`text-xs font-semibold leading-snug ${
                      n.type === 'danger'  ? 'text-rose-300'
                      : n.type === 'warning' ? 'text-amber-300'
                      : n.type === 'success' ? 'text-emerald-300'
                      : 'text-slate-200'
                    }`}>
                      {n.title}
                    </span>
                    {!isRead && <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0 mt-0.5" />}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 leading-snug">{n.desc}</p>
                  {n.tab && (
                    <span className="text-xs text-blue-400 mt-1 inline-block">Tap to view →</span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2 border-t border-slate-800 bg-slate-900/50">
          <p className="text-xs text-slate-600 text-center">
            Smart alerts based on your financial data
          </p>
        </div>
      )}
    </div>
  );
}

// ── Bell button helper (re-exported for use in App.js) ────────────────────────

export function NotificationBell({ onClick, budgetData, netWorthData, goalsData, retirementData, calendarData, userProfile }) {
  const notifications = generateNotifications({ budgetData, netWorthData, goalsData, retirementData, calendarData });
  const readIds = userProfile?.readNotificationIds || [];
  const unreadCount = notifications.filter(n => !readIds.includes(n.id)).length;

  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
      title="Notifications"
    >
      <Bell size={18} />
      {unreadCount > 0 && (
        <span className="absolute top-1 right-1 w-4 h-4 bg-rose-500 rounded-full text-xs flex items-center justify-center text-white font-bold leading-none">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
}
